import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { formatMoney, formatMetric } from '@/utils/currency';
import { ReconcileDialog } from '@/components/admin/ReconcileDialog';
import { MissedTpDialog } from '@/components/admin/MissedTpDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminPositions = () => {
  const [positions, setPositions] = useState([]);
  const [clients, setClients] = useState([]);
  const [instruments, setInstruments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ direction: 'BUY', volume: '', open_price: '', stop_loss: '', take_profit: '', reason: '' });
  const [saving, setSaving] = useState(false);
  const [creatingOpen, setCreatingOpen] = useState(false);
  const [createForm, setCreateForm] = useState({ user_id: '', symbol: '', direction: 'BUY', volume: '0.01', open_price: '', stop_loss: '', take_profit: '', reason: '' });
  const [creating, setCreating] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deletingBusy, setDeletingBusy] = useState(false);
  const [reconciling, setReconciling] = useState(null);
  const [missedTp, setMissedTp] = useState(null);

  const toggleArm = async (position) => {
    const armed = !position.sltp_armed;
    const reason = prompt(`${armed ? 'Arm' : 'Disarm'} automatic SL/TP for this position. Reason / authorization:`);
    if (!reason) return;
    try {
      await axios.post(`${API}/admin/positions/${position.id}/sltp-arm`, { armed, reason });
      toast.success(armed ? 'Automatic SL/TP armed' : 'Automatic SL/TP disarmed');
      fetchPositions();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to update SL/TP');
    }
  };

  const fetchPositions = async () => {
    try {
      const response = await axios.get(`${API}/admin/positions`);
      setPositions(response.data);
    } catch (error) {
      toast.error('Failed to load positions');
    } finally { setLoading(false); }
  };

  useEffect(() => {
    fetchPositions();
    (async () => {
      try {
        const [cRes, iRes] = await Promise.all([
          axios.get(`${API}/admin/clients`),
          axios.get(`${API}/admin/instruments`),
        ]);
        setClients(cRes.data || []);
        setInstruments((iRes.data || []).filter(x => x.trading_enabled !== false && x.watchlist_visible !== false));
      } catch (_e) { /* no-op */ }
    })();
  }, []);

  const closePosition = async (positionId) => {
    const reason = prompt('Reason for closing position:');
    if (!reason) return;
    try {
      await axios.post(`${API}/admin/positions/${positionId}/close`, { reason });
      toast.success('Position closed');
      fetchPositions();
    } catch (error) {
      toast.error('Failed to close position');
    }
  };

  const openEdit = (p) => {
    setForm({
      direction: p.direction || 'BUY',
      volume: p.volume ?? '',
      open_price: p.open_price ?? '',
      stop_loss: p.stop_loss ?? '',
      take_profit: p.take_profit ?? '',
      reason: '',
    });
    setEditing(p);
  };

  const submitCreate = async () => {
    if (!createForm.user_id || !createForm.symbol || !createForm.volume) {
      toast.error('Client, instrument and volume are required'); return;
    }
    setCreating(true);
    try {
      const payload = {
        user_id: createForm.user_id,
        symbol: createForm.symbol,
        direction: createForm.direction,
        volume: Number(createForm.volume),
        open_price: createForm.open_price === '' ? null : Number(createForm.open_price),
        stop_loss: createForm.stop_loss === '' ? null : Number(createForm.stop_loss),
        take_profit: createForm.take_profit === '' ? null : Number(createForm.take_profit),
        reason: createForm.reason || 'Admin created position',
      };
      await axios.post(`${API}/admin/positions`, payload);
      toast.success('Position opened for client');
      setCreatingOpen(false);
      setCreateForm({ user_id: '', symbol: '', direction: 'BUY', volume: '0.01', open_price: '', stop_loss: '', take_profit: '', reason: '' });
      fetchPositions();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to open position');
    } finally { setCreating(false); }
  };

  const submitDelete = async () => {
    if (!deleting) return;
    setDeletingBusy(true);
    try {
      await axios.delete(`${API}/admin/positions/${deleting.id}`, { data: { reason: deleteReason || 'Admin permanent delete' } });
      toast.success('Position permanently deleted');
      setDeleting(null); setDeleteReason('');
      fetchPositions();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Delete failed');
    } finally { setDeletingBusy(false); }
  };

  const saveEdit = async () => {
    if (!editing) return;
    if (!window.confirm('Confirm position modification?')) return;
    setSaving(true);
    try {
      const payload = {
        direction: form.direction,
        volume: form.volume === '' ? null : Number(form.volume),
        open_price: form.open_price === '' ? null : Number(form.open_price),
        stop_loss: form.stop_loss === '' ? null : Number(form.stop_loss),
        take_profit: form.take_profit === '' ? null : Number(form.take_profit),
        reason: form.reason || 'Admin position adjustment',
      };
      await axios.put(`${API}/admin/positions/${editing.id}`, payload);
      toast.success('Position updated — P/L recalculated against the current market price');
      setEditing(null);
      fetchPositions();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Update failed');
    } finally { setSaving(false); }
  };

  const displayed = showAll ? positions : positions.filter(p => p.is_open);

  return (
    <div className="p-8" data-testid="admin-positions-page">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-brand-primary">Positions</h1>
          <p className="text-slate-600 mt-1">{displayed.length} {showAll ? 'total' : 'open'} positions</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => setShowAll(!showAll)} data-testid="admin-toggle-positions-view">
            Show {showAll ? 'Open Only' : 'All'}
          </Button>
          <Button onClick={() => setCreatingOpen(true)} data-testid="admin-new-position-btn">
            + New Position
          </Button>
        </div>
      </div>
      <div className="bg-white rounded-xl border border-slate-200 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-slate-50">
            <tr className="text-left uppercase text-xs tracking-wider text-slate-600">
              <th className="p-4">Client</th>
              <th className="p-4">Symbol</th>
              <th className="p-4">Direction</th>
              <th className="p-4">Volume</th>
              <th className="p-4 text-right">Open Price</th>
              <th className="p-4 text-right">Current</th>
              <th className="p-4 text-right">P/L</th>
              <th className="p-4">Status</th>
              <th className="p-4">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? <tr><td colSpan="9" className="p-8 text-center"><div className="loader mx-auto"></div></td></tr>
              : displayed.length === 0 ? <tr><td colSpan="9" className="p-8 text-center text-slate-500">No positions</td></tr>
              : displayed.map(position => (
                <tr key={position.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="p-4">
                    <div className="font-medium">{position.client_name}</div>
                    <div className="text-xs text-slate-500">{position.client_email}</div>
                  </td>
                  <td className="p-4 font-semibold">{position.symbol}</td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                      position.direction === 'BUY' ? 'bg-trading-bullish-bg text-trading-bullish' : 'bg-trading-bearish-bg text-trading-bearish'
                    }`}>{position.direction}</span>
                  </td>
                  <td className="p-4 tabular-nums">{position.volume}</td>
                  <td className="p-4 text-right tabular-nums">{position.open_price?.toFixed(5)}</td>
                  <td className="p-4 text-right tabular-nums">{position.current_price?.toFixed(5)}</td>
                  <td className={`p-4 text-right font-semibold tabular-nums ${
                    position.profit_loss == null ? 'text-slate-500' : position.profit_loss >= 0 ? 'text-trading-bullish' : 'text-trading-bearish'
                  }`}>{formatMetric(position.profit_loss, position.account_currency)}</td>
                  <td className="p-4">
                    <span className={`px-2 py-1 rounded text-xs font-semibold ${
                      position.is_open ? 'bg-blue-100 text-blue-800' : 'bg-slate-100 text-slate-600'
                    }`}>{position.is_open ? 'OPEN' : 'CLOSED'}</span>
                  </td>
                  <td className="p-4">
                    <div className="flex gap-1">
                      {position.is_open && (
                        <Button size="sm" variant="outline" onClick={() => openEdit(position)} data-testid={`admin-edit-position-${position.id}`}>
                          Edit
                        </Button>
                      )}
                      {position.is_open && (
                        <Button size="sm" variant="outline" onClick={() => closePosition(position.id)} data-testid={`admin-close-position-${position.id}`}>
                          Close
                        </Button>
                      )}
                      {position.is_open && position.price_unavailable && (
                        <Button size="sm" variant="outline" onClick={() => setReconciling(position)} data-testid={`admin-reconcile-position-${position.id}`}>
                          Reconcile
                        </Button>
                      )}
                      {position.is_open && position.take_profit != null && !position.sltp_armed && (
                        <Button size="sm" variant="outline" onClick={() => setMissedTp(position)} data-testid={`admin-missed-tp-${position.id}`}>
                          Missed TP
                        </Button>
                      )}
                      {position.is_open && (position.stop_loss != null || position.take_profit != null) && (
                        <Button size="sm" variant="outline" onClick={() => toggleArm(position)} data-testid={`admin-sltp-arm-${position.id}`}>
                          {position.sltp_armed ? 'SL/TP: Auto' : 'SL/TP: Off'}
                        </Button>
                      )}
                      {!(position.is_open && position.price_unavailable) && (
                      <Button size="sm" variant="destructive" onClick={() => { setDeleteReason(''); setDeleting(position); }} data-testid={`admin-delete-position-${position.id}`}>
                        Delete
                      </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Edit position dialog */}
      <Dialog open={!!editing} onOpenChange={() => setEditing(null)}>
        <DialogContent className="max-w-lg" data-testid="admin-edit-position-dialog">
          <DialogHeader>
            <DialogTitle>Edit Open Position {editing && `— ${editing.symbol}`}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            <div className="text-xs text-slate-500">
              Client: <span className="font-semibold text-slate-800">{editing?.client_name}</span>{' · '}
              Position ID: <span className="font-mono">{editing?.id}</span>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Direction</Label>
                <Select value={form.direction} onValueChange={v => setForm({...form, direction: v})}>
                  <SelectTrigger data-testid="edit-position-direction"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BUY">BUY</SelectItem>
                    <SelectItem value="SELL">SELL</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Volume</Label>
                <Input type="number" step="0.01" value={form.volume} onChange={e=>setForm({...form, volume: e.target.value})} data-testid="edit-position-volume" />
              </div>
              <div>
                <Label>Open Price</Label>
                <Input type="number" step="0.00001" value={form.open_price} onChange={e=>setForm({...form, open_price: e.target.value})} data-testid="edit-position-open-price" />
              </div>
              <div>
                <Label>Stop Loss</Label>
                <Input type="number" step="0.00001" value={form.stop_loss ?? ''} onChange={e=>setForm({...form, stop_loss: e.target.value})} data-testid="edit-position-sl" />
              </div>
              <div className="col-span-2">
                <Label>Take Profit</Label>
                <Input type="number" step="0.00001" value={form.take_profit ?? ''} onChange={e=>setForm({...form, take_profit: e.target.value})} data-testid="edit-position-tp" />
              </div>
              <div className="col-span-2">
                <Label>Reason (audit note)</Label>
                <Textarea rows={2} value={form.reason} onChange={e=>setForm({...form, reason: e.target.value})} placeholder="Optional but recorded in audit log" />
              </div>
            </div>
            <div className="text-xs text-slate-500">
              P/L is recomputed against the current market price. Historical open_price change is audited (previous → new) with client, admin, timestamp.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Cancel</Button>
            <Button onClick={saveEdit} disabled={saving} data-testid="edit-position-save">{saving ? 'Saving…' : 'Save changes'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      {/* Create position dialog */}
      <Dialog open={creatingOpen} onOpenChange={setCreatingOpen}>
        <DialogContent className="max-w-lg" data-testid="admin-new-position-dialog">
          <DialogHeader><DialogTitle>Open Position for Client</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2">
            <div>
              <Label>Client</Label>
              <Select value={createForm.user_id} onValueChange={v => setCreateForm({...createForm, user_id: v})}>
                <SelectTrigger data-testid="new-position-client"><SelectValue placeholder="Select a client" /></SelectTrigger>
                <SelectContent>
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.full_name} — {c.email}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Instrument</Label>
                <Select value={createForm.symbol} onValueChange={v => setCreateForm({...createForm, symbol: v})}>
                  <SelectTrigger data-testid="new-position-symbol"><SelectValue placeholder="Symbol" /></SelectTrigger>
                  <SelectContent>
                    {instruments.map(i => <SelectItem key={i.symbol} value={i.symbol}>{i.symbol} — {i.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Direction</Label>
                <Select value={createForm.direction} onValueChange={v => setCreateForm({...createForm, direction: v})}>
                  <SelectTrigger data-testid="new-position-direction"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BUY">BUY</SelectItem>
                    <SelectItem value="SELL">SELL</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Volume</Label>
                <Input type="number" step="0.01" min="0.01" value={createForm.volume} onChange={e=>setCreateForm({...createForm, volume: e.target.value})} data-testid="new-position-volume" />
              </div>
              <div>
                <Label>Open Price (blank = market)</Label>
                <Input type="number" step="0.00001" value={createForm.open_price} onChange={e=>setCreateForm({...createForm, open_price: e.target.value})} data-testid="new-position-open-price" placeholder="Auto" />
              </div>
              <div>
                <Label>Stop Loss</Label>
                <Input type="number" step="0.00001" value={createForm.stop_loss} onChange={e=>setCreateForm({...createForm, stop_loss: e.target.value})} data-testid="new-position-sl" />
              </div>
              <div>
                <Label>Take Profit</Label>
                <Input type="number" step="0.00001" value={createForm.take_profit} onChange={e=>setCreateForm({...createForm, take_profit: e.target.value})} data-testid="new-position-tp" />
              </div>
              <div className="col-span-2">
                <Label>Reason (audit note)</Label>
                <Textarea rows={2} value={createForm.reason} onChange={e=>setCreateForm({...createForm, reason: e.target.value})} placeholder="Optional but recorded in audit log" />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreatingOpen(false)}>Cancel</Button>
            <Button onClick={submitCreate} disabled={creating} data-testid="new-position-submit">{creating ? 'Opening…' : 'Open Position'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Permanent delete confirmation */}
      <Dialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <DialogContent className="max-w-md" data-testid="admin-delete-position-dialog">
          <DialogHeader><DialogTitle>Delete this position permanently?</DialogTitle></DialogHeader>
          <div className="space-y-3 pt-2 text-sm">
            <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-red-800">
              <strong>This action cannot be undone.</strong> The position record will be removed from the database. This is not the same as closing a trade — no realized P/L will be booked to the client's balance.
            </div>
            {deleting && (
              <div className="text-xs text-slate-600 space-y-1">
                <div>Client: <span className="font-semibold text-slate-900">{deleting.client_name}</span> ({deleting.client_email})</div>
                <div>Position: <span className="font-mono">{deleting.id}</span></div>
                <div>{deleting.symbol} · {deleting.direction} · vol {deleting.volume} · open {deleting.open_price}</div>
              </div>
            )}
            <div>
              <Label>Reason (audit)</Label>
              <Textarea rows={2} value={deleteReason} onChange={e=>setDeleteReason(e.target.value)} placeholder="Why is this position being removed?" data-testid="delete-position-reason" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleting(null)}>Cancel</Button>
            <Button variant="destructive" onClick={submitDelete} disabled={deletingBusy} data-testid="delete-position-confirm">
              {deletingBusy ? 'Deleting…' : 'Delete permanently'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <MissedTpDialog position={missedTp} onClose={() => setMissedTp(null)} onDone={() => { setMissedTp(null); fetchPositions(); }} />
      <ReconcileDialog position={reconciling} onClose={() => setReconciling(null)} onDone={() => { setReconciling(null); fetchPositions(); }} />
    </div>
  );
};

export default AdminPositions;
